import { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { Stack } from '@mui/material';
import isEmpty from 'lodash/isEmpty';

import { useCategories } from 'src/api/hooks';
import { ActionsCard } from 'src/components/TablePage/ActionsCard';
import { PAGE_SIZE, routes } from 'src/constants';
import { Pagination } from 'src/components/Pagination';
import {
    CategoriesTable,
    CategoriesFiltersModal
} from 'src/components/Categories';

export const Categories = () => {
    const { fetchCategories, categories, loading, paging, deleteCategory } =
        useCategories();

    const history = useHistory();
    const MENU_ITEMS = [
        {
            title: 'Add Category',
            onClick: () => history.push(routes.categoriesNew)
        },
        {
            title: 'Export CSV'
        }
    ];

    const { totalPages } = paging || {};
    const [pageNumber, setPageNumber] = useState(0);
    const [searchString, setSearchingString] = useState('');

    const [modalVisible, setModalVisible] = useState(false);
    const hideModal = () => setModalVisible(false);
    const openModal = () => setModalVisible(true);

    const { handleSubmit, control, reset } = useForm<any>();
    const [filterFormState, setFilterFormState] = useState<any>();
    const [filter, setFilter] = useState<string>('');

    const getCategories = (pageNumber: number, innerSearchString?: string) => {
        fetchCategories({
            searchString: innerSearchString ?? searchString,
            pageNumber: `${pageNumber}`,
            pageSize: PAGE_SIZE,
            filter
        });
    };

    useEffect(() => {
        getCategories(pageNumber);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (e: ChangeEvent<unknown>, page: number) => {
        setPageNumber(page - 1);
        getCategories(page - 1);
    };

    const handleSearchChange = (searchString?: string) => {
        setSearchingString(searchString || '');
        getCategories(0, searchString);

        if (pageNumber) {
            setPageNumber(0);
        }
    };

    const handleRefresh = () => {
        setFilterFormState(undefined);
        setFilter('');
        reset({});
        fetchCategories({
            searchString: searchString,
            pageNumber: `${pageNumber}`,
            pageSize: PAGE_SIZE
        });
    };

    const handleDeleteCategory = (id: string) => {
        deleteCategory(id, {
            searchString,
            pageNumber: '0',
            pageSize: PAGE_SIZE
        });
        setPageNumber(0);
    };

    const handleOpenFilterModal = () => {
        openModal();
    };

    const applyFilter = (filter: string) => {
        setPageNumber(0);
        setFilter(filter);
        return fetchCategories({
            searchString,
            pageNumber: '0',
            pageSize: PAGE_SIZE,
            filter
        });
    };

    const handleCancelFilter = () => {
        reset(filterFormState);
        hideModal();
    };

    return (
        <Stack spacing={2}>
            <ActionsCard
                onSearchChange={handleSearchChange}
                onRefresh={handleRefresh}
                openFilter={handleOpenFilterModal}
                actionsMenuItems={MENU_ITEMS}
                title="Billing Categories"
            />
            <CategoriesTable
                data={Object.values(categories)}
                loading={loading}
                onDelete={handleDeleteCategory}
            />
            {!isEmpty(categories) && (
                <Pagination
                    page={pageNumber + 1}
                    onChange={handlePageChange}
                    count={totalPages}
                />
            )}
            <CategoriesFiltersModal
                open={modalVisible}
                onClose={hideModal}
                applyFilter={applyFilter}
                onCancel={handleCancelFilter}
                handleSubmit={handleSubmit}
                setFilterFormState={setFilterFormState}
                control={control}
            />
        </Stack>
    );
};
